import { useState, useEffect, useCallback } from 'react';
import HistoryDashboard from '../dashboard/HistoryDashboard';
import '../../css/SupervisorDashboard.css';
import Cookies from 'js-cookie';
import apiservice from "../../shared/services/ApiService";
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import {
    PieChart, Pie, Cell,
    BarChart, Bar,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const SupervisorDashboard = () => {
    const [sectors, setSectors] = useState([]);
    const [assignedBrigade, setAssignedBrigade] = useState(null);
    const [brigadeMembers, setBrigadeMembers] = useState([]);
    const [selectedSectorId, setSelectedSectorId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [comment, setComment] = useState('');
    const userRole = Cookies.get('role');
    const userId = Cookies.get('userId');
    const [searchTerm, setSearchTerm] = useState('');
    const [newScheduledDate, setNewScheduledDate] = useState('');
    const [updateHistory, setUpdateHistory] = useState(false);
    const [viewMode, setViewMode] = useState('cards');
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const [dateFilter, setDateFilter] = useState('upcoming'); // upcoming, completed, all, custom
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [workTypeFilter, setWorkTypeFilter] = useState('all');
    const [allBrigades, setAllBrigades] = useState([]);

    // ⚡ NUEVO: Estados para coordenadas
    const [coordinatesProgress, setCoordinatesProgress] = useState([]);
    const [selectedCoordinate, setSelectedCoordinate] = useState(null);
    const [showCoordinateModal, setShowCoordinateModal] = useState(false);

    // Colores del sistema
    const COLORS = {
        primary: '#3498db',
        danger: '#e74c3c',
        success: '#2ecc71',
        warning: '#f39c12',
        accent1: '#008f45',
        accent2: '#0057A6',
        pending: '#e74c3c',
        inProcess: '#f39c12',
        completed: '#2ecc71',
    };

    // Estadísticas calculadas
    const statistics = {
        total: sectors.length,
        pendiente: sectors.filter(s => s.status === 'Pendiente').length,
        proceso: sectors.filter(s => s.status === 'Proceso').length,
        listo: sectors.filter(s => s.status === 'Listo').length,
        aceptados: sectors.filter(s => s.assignmentAccepted).length,
        rechazados: sectors.filter(s => !s.assignmentAccepted).length,
    };

    // Datos para gráficos
    const statusData = [
        { name: 'Pendiente', value: statistics.pendiente, color: COLORS.pending },
        { name: 'En Proceso', value: statistics.proceso, color: COLORS.inProcess },
        { name: 'Completado', value: statistics.listo, color: COLORS.completed },
    ];

    const assignmentData = [
        { name: 'Aceptados', value: statistics.aceptados },
        { name: 'Pendientes', value: statistics.rechazados },
    ];

    // ⚡ NUEVO: Cargar progreso de coordenadas
    const fetchCoordinatesProgress = async (sectorId) => {
        try {
            const sector = sectors.find(s => s._id === sectorId);
            if (!sector) return;

            const totalPoints = sector.coordinates?.length || 0;
            const completedCoords = sector.coordinatesProgress || [];

            // Crear array de todas las coordenadas con su estado
            const allCoordinates = [];
            for (let i = 0; i < totalPoints; i++) {
                const completed = completedCoords.find(cp => cp.coordinateIndex === i);
                allCoordinates.push({
                    index: i,
                    latitude: sector.coordinates[i][1],
                    longitude: sector.coordinates[i][0],
                    isCompleted: !!completed,
                    completedBy: completed?.completedBy,
                    completedAt: completed?.completedAt,
                    comment: completed?.comment,
                    workDone: completed?.workDone,
                    issues: completed?.issues,
                    photos: completed?.photos || []
                });
            }

            setCoordinatesProgress(allCoordinates);
        } catch (error) {
            console.error('Error al cargar progreso de coordenadas:', error);
        }
    };

    // ⚡ NUEVO: Cargar historial de coordenadas
    const fetchCoordinateHistory = async (sectorId) => {
        try {
            const response = await apiservice.get(`/api/sectores/${sectorId}/history`);
            response.data.data.history.filter(
                h => h.type === 'coordinate_completed'
            );
        } catch (error) {
            console.error('Error al cargar historial de coordenadas:', error);
        }
    };

    // ⚡ MODIFICAR: handleSelectSector para cargar coordenadas
    const handleSelectSector = async (sectorId) => {
        setSelectedSectorId(sectorId);
        const selectedSector = sectors.find(sector => sector._id === sectorId);

        if (selectedSector?.assignedBrigade) {
            fetchBrigadeMembers(selectedSector.assignedBrigade._id);
        } else {
            setAssignedBrigade(null);
            setBrigadeMembers([]);
        }

        // ⚡ CARGAR PROGRESO DE COORDENADAS
        await fetchCoordinatesProgress(sectorId);
        await fetchCoordinateHistory(sectorId);

        setViewMode('details');
    };

    // ⚡ NUEVO: Abrir detalles de coordenada
    const handleViewCoordinate = (coordinate) => {
        setSelectedCoordinate(coordinate);
        setShowCoordinateModal(true);
    };

    // ⚡ NUEVO: Calcular progreso del sector
    const getSectorProgress = (sectorId) => {
        const sector = sectors.find(s => s._id === sectorId);
        if (!sector || !sector.coordinates) return { percentage: 0, completed: 0, total: 0 };

        const total = sector.coordinates.length;
        const completed = sector.coordinatesProgress?.length || 0;
        const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

        return { percentage, completed, total };
    };

    const fetchSectors = useCallback(async () => {
        try {
            let startDate, endDate;

            // Determinar rango de fechas según filtro
            switch (dateFilter) {
                case 'upcoming':
                    startDate = new Date();
                    endDate = new Date();
                    endDate.setDate(endDate.getDate() + 10);
                    break;
                case 'completed':
                    // Últimos 30 días completados
                    startDate = new Date();
                    startDate.setDate(startDate.getDate() + 30);
                    endDate = new Date();
                    break;
                case 'all':
                    // Últimos 60 días y próximos 30
                    startDate = new Date();
                    startDate.setDate(startDate.getDate() - 60);
                    endDate = new Date();
                    endDate.setDate(endDate.getDate() + 30);
                    break;
                case 'custom':
                    if (!customStartDate || !customEndDate) {
                        toast.warning('Por favor selecciona ambas fechas');
                        return;
                    }
                    startDate = new Date(customStartDate);
                    endDate = new Date(customEndDate);
                    break;
                default:
                    startDate = new Date();
                    endDate = new Date();
                    endDate.setDate(endDate.getDate() + 10);
            }

            const formatoFecha = (fecha) => fecha.toISOString().split('T')[0];
            const response = await apiservice.get(
                `/api/sectores/sectores?fechaInicio=${formatoFecha(startDate)}&fechaFin=${formatoFecha(endDate)}`
            );

            const allSectors = response.data;

            if (userRole === 'Administrador') {
                setSectors(allSectors);
            } else {
                const sectorsForSupervisor = allSectors.filter(sector =>
                    sector.assignedBrigade?.members?.some(m => (m._id || m).toString() === userId)
                );
                setSectors(sectorsForSupervisor);
            }
        } catch (error) {
            console.error('Error al obtener sectores:', error);
            toast.error('Error al cargar los sectores');
        }
    }, [dateFilter, customStartDate, customEndDate, userRole, userId]);

    useEffect(() => {
        fetchSectors();
        if (userRole === 'Administrador') {
            apiservice.get('/api/brigadas').then(res => setAllBrigades(res.data)).catch(() => {});
        }
    }, [fetchSectors, userRole]);

    // Cargar miembros de brigada
    const fetchBrigadeMembers = async (brigadeId) => {
        try {
            const response = await apiservice.get(`/api/brigadas/${brigadeId}`);
            setAssignedBrigade(response.data);
            setBrigadeMembers(response.data.members);
        } catch (error) {
            console.error('Error al obtener miembros de la brigada:', error);
            toast.error('Error al cargar los miembros de la brigada');
        }
    };

    // Generar PDF detallado de un sector específico
    const generateSectorDetailPDF = async (sectorId) => {
        setIsGeneratingPDF(true);
        try {
            const sector = sectors.find(s => s._id === sectorId);
            if (!sector) {
                toast.error('Sector no encontrado');
                return;
            }

            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 15;
            let yPos = 20;

            // ===== PÁGINA 1: INFORMACIÓN GENERAL =====
            // Header principal
            pdf.setFillColor(0, 87, 166);
            pdf.rect(0, 0, pageWidth, 50, 'F');

            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(28);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Reporte Detallado de Sector', pageWidth / 2, 20, { align: 'center' });

            pdf.setFontSize(16);
            pdf.setFont('helvetica', 'normal');
            pdf.text(sector.name, pageWidth / 2, 35, { align: 'center' });

            yPos = 65;
            pdf.setTextColor(0, 0, 0);

            // Información básica
            pdf.setFontSize(18);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Información General', margin, yPos);
            yPos += 10;

            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'normal');

            const basicInfo = [
                ['Nombre:', sector.name],
                ['Estado:', sector.status],
                ['Fecha Programada:', sector.scheduledDate ? new Date(sector.scheduledDate).toLocaleDateString('es-ES') : 'No definida'],
                ['Asignación:', sector.assignmentAccepted ? 'Aceptada' : 'Pendiente'],
                ['Brigada:', sector.assignedBrigade?.name || 'No asignada'],
                ['Descripción:', sector.description || 'Sin descripción']
            ];

            basicInfo.forEach(([label, value]) => {
                pdf.setFont('helvetica', 'bold');
                pdf.text(label, margin, yPos);
                pdf.setFont('helvetica', 'normal');

                // Manejar texto largo
                const textLines = pdf.splitTextToSize(String(value), pageWidth - margin * 2 - 40);
                pdf.text(textLines, margin + 40, yPos);
                yPos += 7 * textLines.length;
            });

            yPos += 10;

            // Progreso de coordenadas
            const progress = getSectorProgress(sectorId);
            pdf.setFontSize(18);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Progreso de Verificación', margin, yPos);
            yPos += 10;

            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'normal');

            // Barra de progreso visual
            const barWidth = pageWidth - (margin * 2);
            const barHeight = 12;

            // Borde de la barra
            pdf.setDrawColor(200, 200, 200);
            pdf.setLineWidth(0.5);
            pdf.rect(margin, yPos, barWidth, barHeight);

            // Progreso completado
            const filledWidth = (progress.completed / progress.total) * barWidth;
            pdf.setFillColor(46, 204, 113);
            pdf.rect(margin, yPos, filledWidth, barHeight, 'F');

            // Texto del progreso
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            const progressText = `${progress.completed}/${progress.total} (${progress.percentage}%)`;
            pdf.text(progressText, margin + barWidth / 2, yPos + 8, { align: 'center' });

            pdf.setTextColor(0, 0, 0);
            yPos += 20;

            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'normal');
            pdf.text(`Total de Puntos: ${progress.total}`, margin, yPos);
            yPos += 7;
            pdf.text(`Puntos Completados: ${progress.completed}`, margin, yPos);
            yPos += 7;
            pdf.text(`Puntos Pendientes: ${progress.total - progress.completed}`, margin, yPos);
            yPos += 15;

            // ===== PÁGINA 2: DETALLES DE COORDENADAS =====
            if (sector.coordinatesProgress && sector.coordinatesProgress.length > 0) {
                pdf.addPage();
                yPos = 20;

                pdf.setFontSize(18);
                pdf.setFont('helvetica', 'bold');
                pdf.text('Detalle de Puntos Completados', margin, yPos);
                yPos += 10;

                pdf.setFontSize(11);
                pdf.setFont('helvetica', 'normal');

                sector.coordinatesProgress.forEach((coord, index) => {
                    if (yPos > pageHeight - 40) {
                        pdf.addPage();
                        yPos = 20;
                    }

                    // Caja para cada coordenada
                    const boxHeight = 35;
                    pdf.setFillColor(248, 249, 250);
                    pdf.rect(margin, yPos - 5, pageWidth - (margin * 2), boxHeight, 'F');

                    // Número de punto
                    pdf.setFillColor(46, 204, 113);
                    pdf.circle(margin + 7, yPos + 2, 5, 'F');
                    pdf.setTextColor(255, 255, 255);
                    pdf.setFont('helvetica', 'bold');
                    pdf.setFontSize(10);
                    pdf.text((coord.coordinateIndex + 1).toString(), margin + 7, yPos + 3, { align: 'center' });

                    // Información del punto
                    pdf.setTextColor(0, 0, 0);
                    pdf.setFont('helvetica', 'bold');
                    pdf.setFontSize(12);
                    pdf.text(`Punto ${coord.coordinateIndex + 1}`, margin + 15, yPos);

                    pdf.setFont('helvetica', 'normal');
                    pdf.setFontSize(10);
                    yPos += 7;

                    const latitude = sector.coordinates[coord.coordinateIndex][1].toFixed(6);
                    const longitude = sector.coordinates[coord.coordinateIndex][0].toFixed(6);
                    pdf.text(`Ubicación: ${latitude}, ${longitude}`, margin + 15, yPos);
                    yPos += 5;

                    if (coord.completedAt) {
                        pdf.text(`Completado: ${new Date(coord.completedAt).toLocaleString('es-ES')}`, margin + 15, yPos);
                        yPos += 5;
                    }

                    if (coord.comment) {
                        pdf.setFont('helvetica', 'italic');
                        const commentLines = pdf.splitTextToSize(`Comentario: ${coord.comment}`, pageWidth - margin * 2 - 20);
                        pdf.text(commentLines, margin + 15, yPos);
                        yPos += 5 * commentLines.length;
                    }

                    if (coord.workDone) {
                        pdf.setFont('helvetica', 'normal');
                        pdf.text(`✓ Trabajo: ${coord.workDone}`, margin + 15, yPos);
                        yPos += 5;
                    }

                    if (coord.issues) {
                        pdf.setTextColor(243, 156, 18);
                        pdf.text(`⚠ Problemas: ${coord.issues}`, margin + 15, yPos);
                        pdf.setTextColor(0, 0, 0);
                        yPos += 5;
                    }

                    if (coord.photos && coord.photos.length > 0) {
                        pdf.text(`📷 ${coord.photos.length} foto(s)`, margin + 15, yPos);
                    }

                    yPos += 10;
                });
            }

            // ===== PÁGINA 3: HISTORIAL =====
            if (sector.history && sector.history.length > 0) {
                pdf.addPage();
                yPos = 20;

                pdf.setFontSize(18);
                pdf.setFont('helvetica', 'bold');
                pdf.text('Historial de Cambios', margin, yPos);
                yPos += 10;

                pdf.setFontSize(10);
                pdf.setFont('helvetica', 'normal');

                sector.history.slice().reverse().forEach((event, index) => {
                    if (yPos > pageHeight - 30) {
                        pdf.addPage();
                        yPos = 20;
                    }

                    // Línea de tiempo
                    pdf.setDrawColor(0, 87, 166);
                    pdf.setLineWidth(0.5);
                    if (index < sector.history.length - 1) {
                        pdf.line(margin + 5, yPos, margin + 5, yPos + 20);
                    }

                    pdf.setFillColor(0, 87, 166);
                    pdf.circle(margin + 5, yPos, 2, 'F');

                    pdf.text(new Date(event.date).toLocaleDateString('es-ES'), margin + 10, yPos);
                    yPos += 5;

                    if (event.comment) {
                        pdf.setFont('helvetica', 'italic');
                        const commentLines = pdf.splitTextToSize(event.comment, pageWidth - margin * 2 - 15);
                        pdf.text(commentLines, margin + 10, yPos);
                        yPos += 5 * commentLines.length;
                    }

                    pdf.setFont('helvetica', 'normal');
                    yPos += 8;
                });
            }

            // Footer en todas las páginas
            const totalPages = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                pdf.setPage(i);
                pdf.setFontSize(8);
                pdf.setTextColor(128, 128, 128);
                pdf.text(
                    `Generado el ${new Date().toLocaleDateString('es-ES')} a las ${new Date().toLocaleTimeString('es-ES')}`,
                    pageWidth / 2,
                    pageHeight - 15,
                    { align: 'center' }
                );
                pdf.text(
                    `Página ${i} de ${totalPages}`,
                    pageWidth / 2,
                    pageHeight - 10,
                    { align: 'center' }
                );
            }

            pdf.save(`sector-${sector.name.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`);
            toast.success('Reporte detallado generado correctamente');

        } catch (error) {
            console.error('Error al generar PDF:', error);
            toast.error('Error al generar el reporte');
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    // Volver a vista de tarjetas
    const handleBackToCards = () => {
        setViewMode('cards');
        setSelectedSectorId(null);
        setCoordinatesProgress([]);
    };

    // Abrir/cerrar modal
    const openModal = () => setIsModalOpen(true);
    const closeModal = () => {
        setIsModalOpen(false);
        setNewStatus('');
        setComment('');
        setNewScheduledDate('');
    };

    // Actualizar sector
    const handleUpdateSector = async () => {
        if (!newStatus) {
            toast.warning('Por favor selecciona un estado');
            return;
        }

        try {
            await apiservice.put(`/api/sectores/${selectedSectorId}`, {
                status: newStatus,
                comment: comment,
                scheduledDate: newScheduledDate || null,
            });

            closeModal();
            await fetchSectors();
            toast.success('Estado del sector actualizado correctamente');
            setUpdateHistory((prev) => !prev);
        } catch (error) {
            console.error('Error al actualizar el sector:', error);
            toast.error('Error al actualizar el estado del sector');
        }
    };

    // Aceptar asignación
    const handleAcceptAssignment = async (sectorId) => {
        try {
            await apiservice.put(`/api/sectores/${sectorId}/accept-assignment`);
            await fetchSectors();
            toast.success('Asignación aceptada correctamente');
            setUpdateHistory((prev) => !prev);
        } catch (error) {
            console.error('Error al aceptar la asignación:', error);
            toast.error('Error al aceptar la asignación');
        }
    };

    // Rechazar asignación
    const handleRejectAssignment = async (sectorId) => {
        try {
            await apiservice.put(`/api/sectores/${sectorId}/reject-assignment`);
            await fetchSectors();
            toast.success('Asignación rechazada correctamente');
            setUpdateHistory((prev) => !prev);
        } catch (error) {
            console.error('Error al rechazar la asignación:', error);
            toast.error('Error al rechazar la asignación');
        }
    };

    // Generar PDF (mantener igual)
    const generatePDF = async () => {
        setIsGeneratingPDF(true);
        try {
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // Header
            pdf.setFillColor(0, 87, 166);
            pdf.rect(0, 0, pageWidth, 40, 'F');

            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(24);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Reporte de Sectores', pageWidth / 2, 20, { align: 'center' });

            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'normal');
            pdf.text(`Fecha: ${new Date().toLocaleDateString('es-ES')}`, pageWidth / 2, 30, { align: 'center' });

            // Estadísticas
            pdf.setTextColor(0, 0, 0);
            pdf.setFontSize(16);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Resumen Ejecutivo', 15, 55);

            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'normal');
            let yPos = 65;

            const stats = [
                { label: 'Total de Sectores', value: statistics.total, color: [52, 152, 219] },
                { label: 'Pendientes', value: statistics.pendiente, color: [231, 76, 60] },
                { label: 'En Proceso', value: statistics.proceso, color: [243, 156, 18] },
                { label: 'Completados', value: statistics.listo, color: [46, 204, 113] },
                { label: 'Asignaciones Aceptadas', value: statistics.aceptados, color: [0, 143, 69] },
            ];

            stats.forEach(stat => {
                pdf.setFillColor(...stat.color);
                pdf.circle(20, yPos - 2, 3, 'F');
                pdf.text(`${stat.label}: ${stat.value}`, 28, yPos);
                yPos += 8;
            });

            // Lista de sectores
            yPos += 10;
            pdf.setFontSize(16);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Detalle de Sectores', 15, yPos);
            yPos += 10;

            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');

            sectors.forEach((sector, index) => {
                if (yPos > pageHeight - 30) {
                    pdf.addPage();
                    yPos = 20;
                }

                const boxHeight = 25;
                pdf.setDrawColor(200, 200, 200);
                pdf.setLineWidth(0.5);
                pdf.rect(15, yPos - 5, pageWidth - 30, boxHeight);

                const statusColor = sector.status === 'Pendiente' ? [231, 76, 60] :
                    sector.status === 'Proceso' ? [243, 156, 18] :
                        [46, 204, 113];
                pdf.setFillColor(...statusColor);
                pdf.circle(20, yPos, 3, 'F');

                pdf.setFont('helvetica', 'bold');
                pdf.text(`${index + 1}. ${sector.name}`, 28, yPos);

                pdf.setFont('helvetica', 'normal');
                pdf.text(`Estado: ${sector.status}`, 28, yPos + 6);
                pdf.text(`Fecha: ${sector.scheduledDate ? new Date(sector.scheduledDate).toLocaleDateString('es-ES') : 'No definida'}`, 28, yPos + 12);
                pdf.text(`Asignación: ${sector.assignmentAccepted ? 'Aceptada' : 'Pendiente'}`, 28, yPos + 18);

                yPos += boxHeight + 5;
            });

            const totalPages = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                pdf.setPage(i);
                pdf.setFontSize(8);
                pdf.setTextColor(128, 128, 128);
                pdf.text(
                    `Página ${i} de ${totalPages}`,
                    pageWidth / 2,
                    pageHeight - 10,
                    { align: 'center' }
                );
            }

            pdf.save(`reporte-sectores-${new Date().toISOString().split('T')[0]}.pdf`);
            toast.success('Reporte PDF generado correctamente');
        } catch (error) {
            console.error('Error al generar PDF:', error);
            toast.error('Error al generar el reporte PDF');
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const getWorkTypeLabel = (type) => {
        switch (type) {
            case 'trimmer': return 'Corte de Césped';
            case 'tree': return 'Poda de Árboles';
            case 'ornamental': return 'Mantenimiento de Ornato';
            case 'mixed': return 'Mixto';
            default: return 'Mixto';
        }
    };

    const generateBrigadeReportPDF = async (brigadeId) => {
        setIsGeneratingPDF(true);
        try {
            const response = await apiservice.get(`/api/sectores/brigade/${brigadeId}/report`);
            const { sectors: brigadeSectors, stats } = response.data;
            const brigade = brigadeSectors[0]?.assignedBrigade || { name: 'Brigada', members: [] };

            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // Header
            pdf.setFillColor(0, 87, 166);
            pdf.rect(0, 0, pageWidth, 45, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(22);
            pdf.setFont('helvetica', 'bold');
            pdf.text(`Reporte de Brigada`, pageWidth / 2, 18, { align: 'center' });
            pdf.setFontSize(16);
            pdf.text(brigade.name || 'Sin nombre', pageWidth / 2, 28, { align: 'center' });
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            pdf.text(`Generado: ${new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}`, pageWidth / 2, 38, { align: 'center' });

            let yPos = 58;

            // Miembros de brigada
            pdf.setTextColor(0, 87, 166);
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Miembros de la Brigada', 15, yPos);
            yPos += 8;
            pdf.setTextColor(80, 80, 80);
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            (brigade.members || []).forEach(member => {
                pdf.text(`• ${member.username || 'Sin nombre'}`, 20, yPos);
                yPos += 6;
            });
            if (!brigade.members?.length) {
                pdf.text('Sin miembros asignados', 20, yPos);
                yPos += 6;
            }

            // Resumen estadístico
            yPos += 6;
            pdf.setTextColor(0, 87, 166);
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Resumen Estadístico', 15, yPos);
            yPos += 10;

            const statBoxW = (pageWidth - 40) / 4;
            const statItems = [
                { label: 'Total', value: stats.total, color: [52, 152, 219] },
                { label: 'Pendientes', value: stats.pendiente, color: [231, 76, 60] },
                { label: 'En Proceso', value: stats.proceso, color: [243, 156, 18] },
                { label: 'Completados', value: stats.listo, color: [46, 204, 113] },
            ];

            statItems.forEach((stat, i) => {
                const x = 15 + i * (statBoxW + 3);
                pdf.setFillColor(...stat.color);
                pdf.roundedRect(x, yPos, statBoxW, 20, 3, 3, 'F');
                pdf.setTextColor(255, 255, 255);
                pdf.setFontSize(16);
                pdf.setFont('helvetica', 'bold');
                pdf.text(String(stat.value), x + statBoxW / 2, yPos + 10, { align: 'center' });
                pdf.setFontSize(8);
                pdf.setFont('helvetica', 'normal');
                pdf.text(stat.label, x + statBoxW / 2, yPos + 16, { align: 'center' });
            });
            yPos += 28;

            // Desglose por tipo de trabajo
            pdf.setTextColor(0, 87, 166);
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Por Tipo de Trabajo', 15, yPos);
            yPos += 8;
            pdf.setTextColor(80, 80, 80);
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');

            const workTypes = [
                { label: 'Corte de Césped', value: stats.byWorkType.trimmer, color: [46, 204, 113] },
                { label: 'Poda de Árboles', value: stats.byWorkType.tree, color: [139, 90, 43] },
                { label: 'Mantenimiento de Ornato', value: stats.byWorkType.ornamental, color: [142, 68, 173] },
                { label: 'Mixto', value: stats.byWorkType.mixed, color: [52, 152, 219] },
            ];

            workTypes.forEach(wt => {
                pdf.setFillColor(...wt.color);
                pdf.circle(20, yPos - 1.5, 2.5, 'F');
                pdf.text(`${wt.label}: ${wt.value}`, 26, yPos);
                yPos += 7;
            });
            yPos += 5;

            // Tabla de sectores
            pdf.setTextColor(0, 87, 166);
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Detalle de Sectores', 15, yPos);
            yPos += 8;

            // Table header
            pdf.setFillColor(0, 87, 166);
            pdf.rect(15, yPos, pageWidth - 30, 8, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(8);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Sector', 18, yPos + 5.5);
            pdf.text('Estado', 80, yPos + 5.5);
            pdf.text('Tipo', 108, yPos + 5.5);
            pdf.text('Fecha', 148, yPos + 5.5);
            pdf.text('Avance', 178, yPos + 5.5);
            yPos += 10;

            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);

            brigadeSectors.forEach((sector, idx) => {
                if (yPos > pageHeight - 25) {
                    pdf.addPage();
                    yPos = 20;
                }

                const bgColor = idx % 2 === 0 ? [245, 247, 250] : [255, 255, 255];
                pdf.setFillColor(...bgColor);
                pdf.rect(15, yPos - 4, pageWidth - 30, 10, 'F');

                const statusColor = sector.status === 'Pendiente' ? [231, 76, 60] :
                    sector.status === 'Proceso' ? [243, 156, 18] : [46, 204, 113];
                pdf.setFillColor(...statusColor);
                pdf.circle(83, yPos - 0.5, 2, 'F');

                pdf.setTextColor(50, 50, 50);
                const sectorName = sector.name.length > 28 ? sector.name.substring(0, 28) + '...' : sector.name;
                pdf.text(sectorName, 18, yPos);
                pdf.text(sector.status, 88, yPos);
                pdf.text(getWorkTypeLabel(sector.workType || 'mixed').substring(0, 18), 108, yPos);
                pdf.text(sector.scheduledDate ? new Date(sector.scheduledDate).toLocaleDateString('es-ES') : '-', 148, yPos);
                pdf.text(`${sector.progress.percentage}%`, 180, yPos);
                yPos += 10;
            });

            // Footer con paginación
            const totalPages = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                pdf.setPage(i);
                pdf.setFontSize(7);
                pdf.setTextColor(150, 150, 150);
                pdf.text(`Gestión Ambiental - Reporte de Brigada: ${brigade.name}`, 15, pageHeight - 8);
                pdf.text(`Página ${i} de ${totalPages}`, pageWidth - 15, pageHeight - 8, { align: 'right' });
            }

            pdf.save(`reporte-brigada-${(brigade.name || 'brigada').replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.pdf`);
            toast.success('Reporte de brigada generado correctamente');
        } catch (error) {
            console.error('Error al generar reporte de brigada:', error);
            toast.error('Error al generar el reporte de brigada');
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const generateExcel = () => {
        try {
            const data = sectors.map(sector => ({
                'Sector': sector.name,
                'Estado': sector.status,
                'Tipo de Trabajo': getWorkTypeLabel(sector.workType || 'mixed'),
                'Brigada': sector.assignedBrigade?.name || 'Sin asignar',
                'Fecha Programada': sector.scheduledDate ? new Date(sector.scheduledDate).toLocaleDateString('es-ES') : '-',
                'Coordenadas Total': sector.coordinates?.length || 0,
                'Coordenadas Completadas': sector.coordinatesProgress?.length || 0,
                'Avance (%)': sector.coordinates?.length > 0
                    ? Math.round((sector.coordinatesProgress?.length || 0) / sector.coordinates.length * 100)
                    : 0,
                'Asignación': sector.assignmentAccepted ? 'Aceptada' : 'Pendiente',
            }));

            const ws = XLSX.utils.json_to_sheet(data);
            ws['!cols'] = [
                { wch: 30 }, { wch: 12 }, { wch: 22 }, { wch: 20 },
                { wch: 16 }, { wch: 12 }, { wch: 18 }, { wch: 10 }, { wch: 12 }
            ];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Sectores');

            // Hoja de resumen
            const resumen = [
                { 'Métrica': 'Total Sectores', 'Valor': statistics.total },
                { 'Métrica': 'Pendientes', 'Valor': statistics.pendiente },
                { 'Métrica': 'En Proceso', 'Valor': statistics.proceso },
                { 'Métrica': 'Completados', 'Valor': statistics.listo },
            ];
            const wsResumen = XLSX.utils.json_to_sheet(resumen);
            wsResumen['!cols'] = [{ wch: 20 }, { wch: 10 }];
            XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen');

            XLSX.writeFile(wb, `reporte-sectores-${new Date().toISOString().split('T')[0]}.xlsx`);
            toast.success('Reporte Excel generado');
        } catch (error) {
            console.error('Error al generar Excel:', error);
            toast.error('Error al generar el reporte Excel');
        }
    };

    const generateBrigadeExcel = async (brigadeId) => {
        try {
            const response = await apiservice.get(`/api/sectores/brigade/${brigadeId}/report`);
            const { sectors: brigadeSectors, stats } = response.data;
            const brigade = brigadeSectors[0]?.assignedBrigade || { name: 'Brigada', members: [] };

            const data = brigadeSectors.map(sector => ({
                'Sector': sector.name,
                'Estado': sector.status,
                'Tipo de Trabajo': getWorkTypeLabel(sector.workType || 'mixed'),
                'Fecha Programada': sector.scheduledDate ? new Date(sector.scheduledDate).toLocaleDateString('es-ES') : '-',
                'Coordenadas Total': sector.progress.totalPoints,
                'Completadas': sector.progress.completedPoints,
                'Avance (%)': sector.progress.percentage,
            }));

            const ws = XLSX.utils.json_to_sheet(data);
            ws['!cols'] = [
                { wch: 30 }, { wch: 12 }, { wch: 22 }, { wch: 16 },
                { wch: 14 }, { wch: 12 }, { wch: 10 }
            ];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Sectores');

            const resumen = [
                { 'Métrica': 'Brigada', 'Valor': brigade.name },
                { 'Métrica': 'Miembros', 'Valor': brigade.members?.length || 0 },
                { 'Métrica': 'Total', 'Valor': stats.total },
                { 'Métrica': 'Pendientes', 'Valor': stats.pendiente },
                { 'Métrica': 'En Proceso', 'Valor': stats.proceso },
                { 'Métrica': 'Completados', 'Valor': stats.listo },
                { 'Métrica': 'Corte Césped', 'Valor': stats.byWorkType.trimmer },
                { 'Métrica': 'Poda Árboles', 'Valor': stats.byWorkType.tree },
                { 'Métrica': 'Ornato', 'Valor': stats.byWorkType.ornamental },
                { 'Métrica': 'Mixto', 'Valor': stats.byWorkType.mixed },
            ];
            const wsResumen = XLSX.utils.json_to_sheet(resumen);
            wsResumen['!cols'] = [{ wch: 20 }, { wch: 15 }];
            XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen');

            XLSX.writeFile(wb, `reporte-brigada-${(brigade.name || 'brigada').replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.xlsx`);
            toast.success('Reporte Excel de brigada generado');
        } catch (error) {
            console.error('Error al generar Excel de brigada:', error);
            toast.error('Error al generar el reporte');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Pendiente': return COLORS.pending;
            case 'Proceso': return COLORS.inProcess;
            case 'Listo': return COLORS.completed;
            default: return '#9E9E9E';
        }
    };

    const filteredSectors = sectors.filter(sector => {
        const matchesSearch = sector.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesWorkType = workTypeFilter === 'all' || (sector.workType || 'mixed') === workTypeFilter;
        return matchesSearch && matchesWorkType;
    });

    return (
        <div className="supervisor-dashboard">
            {/* Header con navegación */}
            <div className="dashboard-header">
                <div className="header-left">
                    <h1 className="dashboard-title">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                        </svg>
                        Panel del Supervisor
                    </h1>
                </div>
                <div className="header-right">
                    <div className="view-toggle-group">
                        <button
                            className={`view-toggle-btn-sm ${viewMode === 'dashboard' ? 'active' : ''}`}
                            onClick={() => setViewMode('dashboard')}
                            title="Dashboard"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
                                <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
                            </svg>
                            <span className="btn-label">Dashboard</span>
                        </button>
                        <button
                            className={`view-toggle-btn-sm ${viewMode === 'cards' ? 'active' : ''}`}
                            onClick={() => setViewMode('cards')}
                            title="Sectores"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" />
                            </svg>
                            <span className="btn-label">Sectores</span>
                        </button>
                        {userRole === 'Administrador' && (
                            <button
                                className={`view-toggle-btn-sm ${viewMode === 'brigades' ? 'active' : ''}`}
                                onClick={() => setViewMode('brigades')}
                                title="Brigadas"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                                <span className="btn-label">Brigadas</span>
                            </button>
                        )}
                    </div>
                    <div className="report-actions">
                        <button
                            className="report-btn report-btn-pdf"
                            onClick={generatePDF}
                            disabled={isGeneratingPDF}
                            title="Exportar PDF"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                            </svg>
                            PDF
                        </button>
                        <button
                            className="report-btn report-btn-excel"
                            onClick={generateExcel}
                            title="Exportar Excel"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="8" y1="13" x2="16" y2="13" />
                                <line x1="8" y1="17" x2="16" y2="17" />
                            </svg>
                            Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* Filtros de fecha */}
            <div className="date-filters-section">
                <div className="filters-container">
                    <button
                        className={`filter-chip ${dateFilter === 'upcoming' ? 'active' : ''}`}
                        onClick={() => setDateFilter('upcoming')}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                            <line x1="16" y1="2" x2="16" y2="6" />
                            <line x1="8" y1="2" x2="8" y2="6" />
                            <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        Próximos
                    </button>

                    <button
                        className={`filter-chip ${dateFilter === 'completed' ? 'active' : ''}`}
                        onClick={() => setDateFilter('completed')}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                        Completados
                    </button>

                    <button
                        className={`filter-chip ${dateFilter === 'all' ? 'active' : ''}`}
                        onClick={() => setDateFilter('all')}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="4" y1="21" x2="4" y2="14" />
                            <line x1="4" y1="10" x2="4" y2="3" />
                            <line x1="12" y1="21" x2="12" y2="12" />
                            <line x1="12" y1="8" x2="12" y2="3" />
                            <line x1="20" y1="21" x2="20" y2="16" />
                            <line x1="20" y1="12" x2="20" y2="3" />
                        </svg>
                        Todos
                    </button>

                    <button
                        className={`filter-chip ${dateFilter === 'custom' ? 'active' : ''}`}
                        onClick={() => setDateFilter('custom')}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                        </svg>
                        Personalizado
                    </button>
                </div>

                {dateFilter === 'custom' && (
                    <div className="custom-date-inputs">
                        <div className="date-input-group">
                            <label>Desde:</label>
                            <input
                                type="date"
                                value={customStartDate}
                                onChange={(e) => setCustomStartDate(e.target.value)}
                                className="date-input"
                            />
                        </div>
                        <div className="date-input-group">
                            <label>Hasta:</label>
                            <input
                                type="date"
                                value={customEndDate}
                                onChange={(e) => setCustomEndDate(e.target.value)}
                                className="date-input"
                            />
                        </div>
                        <button
                            className="apply-filter-btn"
                            onClick={fetchSectors}
                        >
                            Aplicar
                        </button>
                    </div>
                )}
            </div>

            {/* Dashboard View */}
            {viewMode === 'dashboard' && (
                <div className="dashboard-view">
                    {/* Estadísticas Cards */}
                    <div className="stats-grid">
                        <div className="stat-card total">
                            <div className="stat-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                    <circle cx="12" cy="10" r="3" />
                                </svg>
                            </div>
                            <div className="stat-content">
                                <h3>{statistics.total}</h3>
                                <p>Total Sectores</p>
                            </div>
                        </div>

                        <div className="stat-card pending">
                            <div className="stat-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <polyline points="12 6 12 12 16 14" />
                                </svg>
                            </div>
                            <div className="stat-content">
                                <h3>{statistics.pendiente}</h3>
                                <p>Pendientes</p>
                            </div>
                        </div>

                        <div className="stat-card process">
                            <div className="stat-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                </svg>
                            </div>
                            <div className="stat-content">
                                <h3>{statistics.proceso}</h3>
                                <p>En Proceso</p>
                            </div>
                        </div>

                        <div className="stat-card completed">
                            <div className="stat-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                    <polyline points="22 4 12 14.01 9 11.01" />
                                </svg>
                            </div>
                            <div className="stat-content">
                                <h3>{statistics.listo}</h3>
                                <p>Completados</p>
                            </div>
                        </div>
                    </div>

                    {/* Gráficos */}
                    <div className="charts-grid">
                        <div className="chart-card">
                            <h3 className="chart-title">Estado de Sectores</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={statusData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={100}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {statusData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="chart-card">
                            <h3 className="chart-title">Asignaciones</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={assignmentData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="value" fill={COLORS.primary} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Resumen de sectores con progreso */}
                    <div className="summary-table">
                        <h3 className="summary-title">Resumen de Sectores</h3>
                        <div className="table-container">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Sector</th>
                                        <th>Estado</th>
                                        <th>Progreso</th>
                                        <th>Fecha</th>
                                        <th>Asignación</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sectors.map(sector => {
                                        const progress = getSectorProgress(sector._id);
                                        return (
                                            <tr key={sector._id}>
                                                <td className="sector-name-cell">{sector.name}</td>
                                                <td>
                                                    <span className={`status-badge status-${sector.status.toLowerCase()}`}>
                                                        {sector.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="progress-cell">
                                                        <div className="progress-bar-mini">
                                                            <div
                                                                className="progress-fill-mini"
                                                                style={{
                                                                    width: `${progress.percentage}%`,
                                                                    backgroundColor: progress.percentage === 100 ? COLORS.success : COLORS.warning
                                                                }}
                                                            ></div>
                                                        </div>
                                                        <span className="progress-text-mini">
                                                            {progress.completed}/{progress.total} ({progress.percentage}%)
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>{sector.scheduledDate ? new Date(sector.scheduledDate).toLocaleDateString('es-ES') : '-'}</td>
                                                <td>
                                                    <span className={`assignment-badge ${sector.assignmentAccepted ? 'accepted' : 'pending'}`}>
                                                        {sector.assignmentAccepted ? 'Aceptada' : 'Pendiente'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button
                                                        className="action-btn view"
                                                        onClick={() => handleSelectSector(sector._id)}
                                                    >
                                                        Ver
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Cards View - Mantener igual pero agregar progreso */}
            {viewMode === 'cards' && (
                <div className="cards-view">
                    {/* Tabs de Tipo de Trabajo */}
                    <div className="work-type-tabs">
                        {[
                            { value: 'all', label: 'Todos' },
                            { value: 'trimmer', label: 'Corte de Cesped' },
                            { value: 'tree', label: 'Poda de Arboles' },
                            { value: 'ornamental', label: 'Ornato' },
                            { value: 'mixed', label: 'Mixto' }
                        ].map(tab => (
                            <button
                                key={tab.value}
                                className={`work-type-tab ${workTypeFilter === tab.value ? 'active' : ''}`}
                                onClick={() => setWorkTypeFilter(tab.value)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div className="search-section">
                        <div className="search-box">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8" />
                                <path d="m21 21-4.35-4.35" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Buscar sector..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="sector-cards-grid">
                        {filteredSectors.map(sector => {
                            const progress = getSectorProgress(sector._id);
                            return (
                                <div
                                    key={sector._id}
                                    className="sector-card-modern"
                                    onClick={() => handleSelectSector(sector._id)}
                                >
                                    <div className="card-header">
                                        <div className="card-icon" style={{ backgroundColor: getStatusColor(sector.status) }}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                                <circle cx="12" cy="10" r="3" />
                                            </svg>
                                        </div>
                                        <span className={`status-dot ${sector.status.toLowerCase()}`}></span>
                                    </div>

                                    <div className="card-body">
                                        <h3 className="card-title">{sector.name}</h3>

                                        {/* ⚡ NUEVO: Barra de progreso en tarjeta */}
                                        <div className="card-progress">
                                            <div className="progress-bar-card">
                                                <div
                                                    className="progress-fill-card"
                                                    style={{
                                                        width: `${progress.percentage}%`,
                                                        backgroundColor: progress.percentage === 100 ? COLORS.success : COLORS.warning
                                                    }}
                                                ></div>
                                            </div>
                                            <span className="progress-label">
                                                {progress.completed}/{progress.total} puntos ({progress.percentage}%)
                                            </span>
                                        </div>

                                        <div className="card-info">
                                            <div className="info-item">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <polyline points="12 6 12 12 16 14" />
                                                </svg>
                                                <span>{sector.status}</span>
                                            </div>
                                            <div className="info-item">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                    <line x1="16" y1="2" x2="16" y2="6" />
                                                    <line x1="8" y1="2" x2="8" y2="6" />
                                                    <line x1="3" y1="10" x2="21" y2="10" />
                                                </svg>
                                                <span>{sector.scheduledDate ? new Date(sector.scheduledDate).toLocaleDateString('es-ES') : 'Sin fecha'}</span>
                                            </div>
                                        </div>

                                        {sector.url && (

                                            <a href={sector.url}
                                                className="location-link"
                                                onClick={(e) => e.stopPropagation()}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                                    <circle cx="12" cy="10" r="3" />
                                                </svg>
                                                Ver ubicación
                                            </a>
                                        )}
                                    </div>

                                    <div className="card-footer">
                                        {sector.assignmentAccepted ? (
                                            <button
                                                className="assignment-btn reject"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRejectAssignment(sector._id);
                                                }}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <line x1="15" y1="9" x2="9" y2="15" />
                                                    <line x1="9" y1="9" x2="15" y2="15" />
                                                </svg>
                                                Rechazar
                                            </button>
                                        ) : (
                                            <button
                                                className="assignment-btn accept"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleAcceptAssignment(sector._id);
                                                }}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                                    <polyline points="22 4 12 14.01 9 11.01" />
                                                </svg>
                                                Aceptar
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Details View - AGREGAR SECCIÓN DE COORDENADAS */}
            {viewMode === 'details' && selectedSectorId && (
                <div className="details-view">
                    <button className="back-button" onClick={handleBackToCards}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="19" y1="12" x2="5" y2="12" />
                            <polyline points="12 19 5 12 12 5" />
                        </svg>
                        Volver a Sectores
                    </button>

                    <div className="details-container">
                        <div className="details-header">
                            <div className="details-header-left">
                                <h2>{sectors.find(s => s._id === selectedSectorId)?.name}</h2>
                                <span className={`status-badge-large ${sectors.find(s => s._id === selectedSectorId)?.status.toLowerCase()}`}>
                                    {sectors.find(s => s._id === selectedSectorId)?.status}
                                </span>
                            </div>
                            <div className="details-header-right">
                                {sectors.find(s => s._id === selectedSectorId)?.assignmentAccepted ? (
                                    <button
                                        className="action-button reject-btn"
                                        onClick={() => handleRejectAssignment(selectedSectorId)}
                                    >
                                        Rechazar Asignación
                                    </button>
                                ) : (
                                    <button
                                        className="action-button accept-btn"
                                        onClick={() => handleAcceptAssignment(selectedSectorId)}
                                    >
                                        Aceptar Asignación
                                    </button>
                                )}
                                <button className="action-button edit-btn" onClick={openModal}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                    Editar Estado
                                </button>
                                <button
                                    className="report-btn report-btn-pdf"
                                    onClick={() => generateSectorDetailPDF(selectedSectorId)}
                                    disabled={isGeneratingPDF}
                                    title="Reporte detallado del sector"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                    </svg>
                                    PDF
                                </button>
                            </div>
                        </div>

                        {/* ⚡ NUEVO: Sección de Progreso de Coordenadas */}
                        {coordinatesProgress.length > 0 && (
                            <div className="coordinates-section">
                                <h3 className="section-title">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                        <circle cx="12" cy="10" r="3" />
                                    </svg>
                                    Progreso de Puntos de Verificación
                                </h3>

                                {/* Gráfico de progreso */}
                                <div className="progress-chart">
                                    <ResponsiveContainer width="100%" height={150}>
                                        <BarChart data={[
                                            {
                                                name: 'Progreso',
                                                completados: coordinatesProgress.filter(c => c.isCompleted).length,
                                                pendientes: coordinatesProgress.filter(c => !c.isCompleted).length
                                            }
                                        ]}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Legend />
                                            <Bar dataKey="completados" fill={COLORS.success} name="Completados" />
                                            <Bar dataKey="pendientes" fill={COLORS.pending} name="Pendientes" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* Lista de coordenadas */}
                                <div className="coordinates-grid">
                                    {coordinatesProgress.map((coord, index) => (
                                        <div
                                            key={index}
                                            className={`coordinate-card ${coord.isCompleted ? 'completed' : 'pending'}`}
                                            onClick={() => coord.isCompleted && handleViewCoordinate(coord)}
                                            style={{ cursor: coord.isCompleted ? 'pointer' : 'default' }}
                                        >
                                            <div className="coordinate-header">
                                                <div className="coordinate-number">
                                                    {coord.isCompleted ? (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                                                            <polyline points="20 6 9 17 4 12" />
                                                        </svg>
                                                    ) : (
                                                        <span>{index + 1}</span>
                                                    )}
                                                </div>
                                                <span className={`coordinate-status ${coord.isCompleted ? 'completed' : 'pending'}`}>
                                                    {coord.isCompleted ? 'Completado' : 'Pendiente'}
                                                </span>
                                            </div>
                                            <div className="coordinate-body">
                                                <div className="coordinate-info">
                                                    <span className="coordinate-label">Punto {index + 1}</span>
                                                    <span className="coordinate-coords">
                                                        {coord.latitude.toFixed(5)}, {coord.longitude.toFixed(5)}
                                                    </span>
                                                </div>
                                                {coord.isCompleted && (
                                                    <div className="coordinate-meta">
                                                        <span className="completion-date">
                                                            {new Date(coord.completedAt).toLocaleDateString('es-ES')}
                                                        </span>
                                                        {coord.comment && (
                                                            <span className="has-comment">
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                                                </svg>
                                                                Comentarios
                                                            </span>
                                                        )}
                                                        {coord.photos && coord.photos.length > 0 && (
                                                            <span className="has-photos">
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                                                    <circle cx="8.5" cy="8.5" r="1.5" />
                                                                    <polyline points="21 15 16 10 5 21" />
                                                                </svg>
                                                                {coord.photos.length} foto(s)
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {assignedBrigade && (
                            <div className="brigade-section">
                                <h3 className="section-title">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                        <circle cx="9" cy="7" r="4" />
                                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                    </svg>
                                    Brigada: {assignedBrigade.name}
                                </h3>
                                <div className="members-grid">
                                    {brigadeMembers.map((member) => (
                                        <div key={member._id} className="member-card-modern">
                                            <div className="member-avatar-large">
                                                {member.username.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="member-info">
                                                <h4>{member.username}</h4>
                                                <p>{member.email}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="history-section">
                            <h3 className="section-title">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <polyline points="12 6 12 12 16 14" />
                                </svg>
                                Historial del Sector
                            </h3>
                            <HistoryDashboard sectorId={selectedSectorId} updateHistory={updateHistory} />
                        </div>
                    </div>
                </div>
            )}

            {/* ⚡ NUEVO: Modal de Detalles de Coordenada */}
            {showCoordinateModal && selectedCoordinate && (
                <div className="modal-overlay-modern">
                    <div className="modal-content-modern coordinate-modal">
                        <div className="modal-header-modern">
                            <h3>Detalles del Punto {selectedCoordinate.index + 1}</h3>
                            <button
                                className="modal-close-btn"
                                onClick={() => {
                                    setShowCoordinateModal(false);
                                    setSelectedCoordinate(null);
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>

                        <div className="modal-body-modern">
                            <div className="coordinate-detail-section">
                                <h4>📍 Ubicación</h4>
                                <p>Latitud: {selectedCoordinate.latitude.toFixed(6)}</p>
                                <p>Longitud: {selectedCoordinate.longitude.toFixed(6)}</p>
                                <p>Completado: {new Date(selectedCoordinate.completedAt).toLocaleString('es-ES')}</p>
                            </div>

                            {selectedCoordinate.comment && (
                                <div className="coordinate-detail-section">
                                    <h4>💬 Comentarios</h4>
                                    <p>{selectedCoordinate.comment}</p>
                                </div>
                            )}

                            {selectedCoordinate.workDone && (
                                <div className="coordinate-detail-section">
                                    <h4>✅ Trabajo Realizado</h4>
                                    <p>{selectedCoordinate.workDone}</p>
                                </div>
                            )}

                            {selectedCoordinate.issues && (
                                <div className="coordinate-detail-section">
                                    <h4>⚠️ Problemas Encontrados</h4>
                                    <p>{selectedCoordinate.issues}</p>
                                </div>
                            )}

                            {selectedCoordinate.photos && selectedCoordinate.photos.length > 0 && (
                                <div className="coordinate-detail-section">
                                    <h4>📷 Fotografías ({selectedCoordinate.photos.length})</h4>
                                    <div className="photos-grid">
                                        {selectedCoordinate.photos.map((photo, index) => (
                                            <img
                                                key={index}
                                                src={photo}
                                                alt={`Foto ${index + 1}`}
                                                className="photo-thumbnail"
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer-modern">
                            <button
                                className="modal-btn cancel-btn"
                                onClick={() => {
                                    setShowCoordinateModal(false);
                                    setSelectedCoordinate(null);
                                }}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Vista Resumen por Brigada (Admin) */}
            {viewMode === 'brigades' && userRole === 'Administrador' && (
                <div className="brigades-overview">
                    <h2 className="section-title">Resumen por Brigada</h2>
                    <div className="brigades-grid">
                        {allBrigades.map(brigade => {
                            const brigadeSectors = sectors.filter(s =>
                                s.assignedBrigade && (s.assignedBrigade._id || s.assignedBrigade) === brigade._id
                            );
                            const pending = brigadeSectors.filter(s => s.status === 'Pendiente').length;
                            const inProcess = brigadeSectors.filter(s => s.status === 'Proceso').length;
                            const completed = brigadeSectors.filter(s => s.status === 'Listo').length;
                            const total = brigadeSectors.length;

                            return (
                                <div key={brigade._id} className="brigade-overview-card">
                                    <div className="brigade-overview-header">
                                        <div className="brigade-avatar">
                                            {brigade.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <h3>{brigade.name}</h3>
                                            <span className="brigade-member-count">
                                                {brigade.members?.length || 0} miembros
                                            </span>
                                        </div>
                                    </div>
                                    <div className="brigade-stats-mini">
                                        <div className="brigade-stat pending">
                                            <span className="stat-number">{pending}</span>
                                            <span className="stat-label">Pendientes</span>
                                        </div>
                                        <div className="brigade-stat process">
                                            <span className="stat-number">{inProcess}</span>
                                            <span className="stat-label">En Proceso</span>
                                        </div>
                                        <div className="brigade-stat completed">
                                            <span className="stat-number">{completed}</span>
                                            <span className="stat-label">Completados</span>
                                        </div>
                                        <div className="brigade-stat total">
                                            <span className="stat-number">{total}</span>
                                            <span className="stat-label">Total</span>
                                        </div>
                                    </div>
                                    {total > 0 && (
                                        <div className="brigade-progress-bar">
                                            <div
                                                className="brigade-progress-fill"
                                                style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }}
                                            ></div>
                                        </div>
                                    )}
                                    {brigadeSectors.length > 0 && (
                                        <div className="brigade-sectors-list">
                                            {brigadeSectors.slice(0, 5).map(s => (
                                                <div key={s._id} className="brigade-sector-item" onClick={() => {
                                                    setSelectedSectorId(s._id);
                                                    setViewMode('details');
                                                }}>
                                                    <span className={`status-dot ${s.status === 'Listo' ? 'completed' : s.status === 'Proceso' ? 'process' : 'pending'}`}></span>
                                                    <span className="sector-name-mini">{s.name}</span>
                                                    <span className={`status-badge-mini ${s.status.toLowerCase()}`}>{s.status}</span>
                                                </div>
                                            ))}
                                            {brigadeSectors.length > 5 && (
                                                <div className="more-sectors">+{brigadeSectors.length - 5} mas</div>
                                            )}
                                        </div>
                                    )}
                                    <div className="brigade-report-actions">
                                        <button
                                            className="report-btn report-btn-pdf"
                                            onClick={() => generateBrigadeReportPDF(brigade._id)}
                                            disabled={isGeneratingPDF}
                                            title="Reporte PDF"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14 2 14 8 20 8" />
                                            </svg>
                                            PDF
                                        </button>
                                        <button
                                            className="report-btn report-btn-excel"
                                            onClick={() => generateBrigadeExcel(brigade._id)}
                                            title="Reporte Excel"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14 2 14 8 20 8" />
                                                <line x1="8" y1="13" x2="16" y2="13" />
                                                <line x1="8" y1="17" x2="16" y2="17" />
                                            </svg>
                                            Excel
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Modal de edición de sector - mantener igual */}
            {isModalOpen && (
                <div className="modal-overlay-modern">
                    <div className="modal-content-modern">
                        <div className="modal-header-modern">
                            <h3>Editar Estado del Sector</h3>
                            <button className="modal-close-btn" onClick={closeModal}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>

                        <div className="modal-body-modern">
                            <div className="form-group-modern">
                                <label>Estado</label>
                                <select
                                    value={newStatus}
                                    onChange={(e) => setNewStatus(e.target.value)}
                                    className="form-select-modern"
                                >
                                    <option value="">Selecciona un estado</option>
                                    <option value="Pendiente">Pendiente</option>
                                    <option value="Proceso">En Proceso</option>
                                    <option value="Listo">Completado</option>
                                </select>
                            </div>

                            <div className="form-group-modern">
                                <label>Comentario</label>
                                <textarea
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    className="form-textarea-modern"
                                    rows="4"
                                    placeholder="Escribe un comentario sobre este cambio..."
                                />
                            </div>

                            <div className="form-group-modern">
                                <label>Fecha Programada</label>
                                <input
                                    type="date"
                                    value={newScheduledDate}
                                    onChange={(e) => setNewScheduledDate(e.target.value)}
                                    className="form-input-modern"
                                />
                            </div>
                        </div>

                        <div className="modal-footer-modern">
                            <button className="modal-btn cancel-btn" onClick={closeModal}>
                                Cancelar
                            </button>
                            <button className="modal-btn confirm-btn" onClick={handleUpdateSector}>
                                Actualizar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SupervisorDashboard;